import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { DealStatus, TimelineEvent } from '../../models/deal.models';
import { NgClass } from '@angular/common';
import {
  AbilityService,
  ConfirmHelperService,
  NgbModalService,
  rxResourceFixed,
  DealStatusWithSendEmail,
  DealStatusLabels,
  DEAL_STATUS_TIMELINE_ORDER,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { of } from 'rxjs';
import { SalesApsService } from '../../services/sales-aps.service';
import { SalesService } from '../../services/sales.service';
import { WelcomeEmailStatusDto } from '../../models/email-templates.models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { ReactiveFormsModule } from '@angular/forms';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { format } from 'date-fns/format';
import { WelcomeEmailSendDialogComponent } from './welcome-email-send-dialog/welcome-email-send-dialog.component';

const EMPTY_WELCOME_EMAIL_STATUS: WelcomeEmailStatusDto = {
  isWelcomeEmailSent: false,
  isWelcomeEmailSkipped: false,
  canSendWelcomeEmail: false
};

@Component({
  selector: 'lib-deal-status-component',
  imports: [NgClass, DatePicker, SvgIconsComponent, ReactiveFormsModule, ConfirmDialogModule],
  templateUrl: './deal-status.component.html',
  styleUrl: './deal-status.component.scss',
  standalone: true
})
export class DealStatusComponent implements AfterViewInit {
  readonly DealStatusWithSendEmail = DealStatusWithSendEmail;
  readonly DealStatusLabels = DealStatusLabels;

  dealId = input.required<string | null>();
  dealStatus = input.required<DealStatus | null>();
  _salesApsService = inject(SalesApsService);
  private readonly statusOrder = DEAL_STATUS_TIMELINE_ORDER.map(id => ({
    id,
    label: DealStatusLabels[id as DealStatusWithSendEmail]
  }));
  #abilityService = inject(AbilityService);
  #confirmationService = inject(ConfirmHelperService);
  ability = this.#abilityService.ability;
  #salesService = inject(SalesService);
  #modalService = inject(NgbModal);
  #ngbModalService = inject(NgbModalService);
  @ViewChild('closeLotModal') closeLotModal!: TemplateRef<unknown>;
  @ViewChild('arrowsContainer') arrowsContainer!: ElementRef;

  ngAfterViewInit(): void {
    this.scrollToCurrentStatus();
  }

  constructor() {
    effect(() => {
      this.scrollToCurrentStatus();
    });
  }

  welcomeEmailStatus = rxResourceFixed<WelcomeEmailStatusDto, { dealId: string }>({
    params: () => ({ dealId: this.dealId() ?? '' }),
    stream: ({ params }) => {
      if (params.dealId) {
        return this._salesApsService.welcomeEmailStatus(params.dealId, false);
      }
      return of(EMPTY_WELCOME_EMAIL_STATUS);
    }
  });

  isWelcomeEmailSent = computed(() => {
    const status = this.welcomeEmailStatus.value();
    return !!(status?.isWelcomeEmailSent || status?.isWelcomeEmailSkipped);
  });

  canSendWelcomeEmail = computed(() => !!this.welcomeEmailStatus.value()?.canSendWelcomeEmail);

  events = computed<TimelineEvent[]>(() => {
    const currentIndex = this.statusOrder.findIndex(s => s.id.toUpperCase() === this.dealStatus()?.id?.toUpperCase());
    const emailSent = this.isWelcomeEmailSent();
    return this.statusOrder.map((s, i) => ({
      status: s.label,
      // Welcome Email is manual — only mark it complete when actually sent/skipped,
      // not when a later deal status would otherwise pull it along.
      completed: s.id === DealStatusWithSendEmail.SendEmail ? emailSent : currentIndex >= i
    }));
  });

  showSendEmailButton = computed(() => {
    return (
      this.dealStatus()?.id !== DealStatusWithSendEmail.Cancelled.toString().toLowerCase() &&
      this.canSendWelcomeEmail() &&
      this.ability.can('send-welcome-email', 'deals') &&
      !this.isWelcomeEmailSent() &&
      this.welcomeEmailStatus.status() === 'resolved'
    );
  });

  showMarkClosedButton = computed(() => {
    return (
      this.dealStatus()?.id !== DealStatusWithSendEmail.Cancelled.toString().toLowerCase() &&
      this.dealStatus()?.id === DealStatusWithSendEmail.UnderContract.toString().toLowerCase() &&
      this.isWelcomeEmailSent() &&
      this.ability.can('mark-deal-closed', 'deals')
    );
  });

  markDealClosedConfirmation(type: 'close-lot' | 'skip-welcome-email', event: Event) {
    this.#confirmationService
      .showModal({
        icon: 'confirm-icon',
        message:
          type === 'skip-welcome-email'
            ? 'Do you want to skip sending welcome email to the buyer? This action is permanent and cannot be undone.'
            : 'Do you want to close this lot? This action is permanent and cannot be undone.',
        header: type === 'skip-welcome-email' ? 'Skip Welcome Email?' : 'Close Lot?',
        acceptLabel: type === 'skip-welcome-email' ? 'Skip Welcome Email' : 'Close Lot',
        acceptSeverity: 'primary',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          if (type === 'skip-welcome-email') {
            this.sendWelcomeEmail(true);
          } else {
            this.markDealClosed();
          }
        }
      });
  }

  openSendWelcomeEmailDialog(): void {
    const dealId = this.dealId();
    if (!dealId) return;

    const modalRef = this.#modalService.open(WelcomeEmailSendDialogComponent, {
      ...this.#ngbModalService.centerModal,
      size: 'xl',
      scrollable: true
    });
    modalRef.componentInstance.dealId = dealId;
    modalRef.closed.subscribe(sent => {
      if (sent) this.welcomeEmailStatus.reload();
    });
  }

  sendWelcomeEmail(skip: boolean): void {
    const dealId = this.dealId() ?? '';
    if (!dealId) return;

    this._salesApsService.sendWelcomeEmail(dealId, skip).subscribe({
      next: () => {
        this.welcomeEmailStatus.reload();
      }
    });
  }

  markDealClosed(): void {
    if (!this._salesApsService.form.value.closingDate) {
      return;
    }

    this.#salesService
      .markAsClosed(this.dealId() ?? '', format(this._salesApsService.form.value.closingDate, 'yyyy-MM-dd') ?? '')
      .subscribe({
        next: () => {
          this._salesApsService.realoadDeal.next(null);
          this.#modalService.dismissAll();
        }
      });
  }

  openCloseLotModal(): void {
    this.#modalService.open(this.closeLotModal, this.#ngbModalService.centerModal);
  }

  lastCompletedEventIndex = computed(() => {
    const _events = this.events();
    for (let i = _events.length - 1; i >= 0; i--) {
      if ((_events[i] as any).completed === true) return i;
    }
    return -1;
  });

  pendingEventIndex = computed(() => this.lastCompletedEventIndex() + 1);

  private scrollToCurrentStatus(): void {
    // Scroll to show the current status in the center on small screens
    setTimeout(() => {
      const arrowsElement = this.arrowsContainer?.nativeElement;
      if (!arrowsElement) return;

      const currentArrow = arrowsElement.querySelector('.arrow.current');
      if (currentArrow) {
        currentArrow.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 0);
  }
}
